import { useTranslation } from 'react-i18next';
import { TextCell, ActionButtonCell } from '@/components/tables/contentFields';
import { NonSortableColumnHeader } from '@/components/tables/NonSortableColumnHeader';
import EmptyTableView from '@/components/tables/EmptyTableView';
import TableLoadingView from '@/components/tables/TableLoadingView';
import Table, { TableHeader, TableBody } from '@/components/tables/TableBasics';
import type { DeviceModel } from '../types';

interface DeviceModelsTableProps {
  models: DeviceModel[];
  isLoading: boolean;
  onManageFirmware: (model: DeviceModel) => void;
}

export function DeviceModelsTable({
  models,
  isLoading,
  onManageFirmware,
}: DeviceModelsTableProps) {
  const { t } = useTranslation();

  if (isLoading && models.length === 0) {
    return (
      <TableLoadingView isLoading={isLoading} rows={5} columns={3} withHeader={true} />
    );
  }

  if (models.length === 0) {
    return <EmptyTableView />;
  }

  return (
    <div className="relative grow min-h-0">
      {isLoading && (
        <div className="absolute top-2 right-2 z-10">
          <TableLoadingView isLoading={isLoading} rows={0} columns={0} withHeader={false} />
        </div>
      )}
      <Table className="min-h-0 h-full">
        <TableHeader>
          <NonSortableColumnHeader
            label={t('deviceModels.table.columns.id')}
            minWidth="80px"
          />
          <NonSortableColumnHeader
            label={t('deviceModels.table.columns.name')}
            minWidth="200px"
          />
          <NonSortableColumnHeader
            label={t('deviceModels.table.columns.firmware')}
            minWidth="150px"
          />
        </TableHeader>
        <TableBody>
          {models.map((model) => (
            <tr key={model.id} className="hover:bg-surface-darker/50">
              <TextCell value={String(model.id)} />
              <TextCell value={model.name} />
              <ActionButtonCell
                text={t('deviceModels.table.manageFirmware')}
                onClick={(e) => {
                  e.stopPropagation();
                  onManageFirmware(model);
                }}
              />
            </tr>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
